'use client';

// Shared client helper to launch the local Lead Scraper and open its login.
// Used by the Find Leads buttons and the sidebar "Get Leads" item so the
// behaviour is identical everywhere.

import { toast } from '@/lib/toast';

export async function launchLeadScraper() {
    // In the DESKTOP (Electron) app, window.open() hands the address to Windows,
    // so opening an EMPTY address ('') triggers a "no program to open this" error.
    // Only the browser benefits from pre-reserving a blank tab (popup-blocker
    // dodge) — in the desktop app we skip it and just open the real URL at the end.
    const isElectron = typeof navigator !== 'undefined' && /electron/i.test(navigator.userAgent || '');
    const tab = (!isElectron && typeof window !== 'undefined') ? window.open('', '_blank') : null;
    if (tab) {
        try { tab.document.write('<title>Lead Scraper</title><body style="font-family:system-ui;background:#f7f9fc;color:#334155;display:flex;align-items:center;justify-content:center;height:100vh;margin:0"><div style="text-align:center"><div style="font-size:34px">🎯</div><h2>Starting the lead scraper…</h2><p style="color:#64748b">This can take 10–30 seconds. This tab will open the login automatically.</p></div></body>'); } catch {}
    }
    // finish() always opens a REAL http URL — never an empty string — so the
    // desktop app can hand it to the OS safely (opens your default browser).
    const finish = (url) => { if (tab) { try { tab.location = url; } catch { window.open(url, '_blank'); } } else { window.open(url, '_blank'); } };
    try {
        const status = await fetch('/api/lead-scraper/launch').then(r => r.json()).catch(() => ({}));
        const url = status.url || 'http://localhost:3100';
        if (status.running) { finish(url); return; }
        if (status.configured === false) { if (tab) tab.close(); toast('Set your lead-scraper folder path in Settings first.', 'error'); return; }

        toast('Starting the lead scraper… this can take 10–30 seconds.', 'info');
        // Kick off the launch. We don't await it (the POST can hold the connection
        // for the whole cold start), but we DO capture a hard failure (bad folder,
        // spawn error) so we can surface the real reason instead of a generic
        // timeout. `done` stops the poll loop from racing the failure toast.
        let hardError = null, done = false;
        fetch('/api/lead-scraper/launch', { method: 'POST' })
            .then(r => r.json()).catch(() => null)
            .then(res => { if (res && res.success === false && res.error && !res.url) hardError = res.error; });
        for (let i = 0; i < 30 && !done; i++) {
            await new Promise(r => setTimeout(r, 2000));
            if (hardError) { if (tab) tab.close(); toast(hardError, 'error'); return; }
            const s = await fetch('/api/lead-scraper/launch').then(r => r.json()).catch(() => ({}));
            if (s.running) { done = true; finish(url); toast('Lead scraper ready — log in to start scraping.', 'success'); return; }
        }
        if (tab) tab.close();
        toast(hardError || 'Scraper is taking longer than usual. Give it a moment and click again.', 'error');
    } catch (e) {
        if (tab) tab.close();
        toast('Could not start the lead scraper.', 'error');
    }
}
